import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Drawer,
  Form,
  Input,
  message,
  Row,
  Select,
} from "antd";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import API from "../../../api";
import ConfirmationModal from "../../../components/ConfirmationModel";

const { Option } = Select;

const CustomerPaymentDrawer = ({
  open,
  setDrawerVisible,
  selectedPayment,
  handleSubmit,
  selectedCustomer,
}) => {
  const [form] = Form.useForm();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [invoiceData, setInvoiceData] = useState({
    totalAmount: 0,
    paidAmount: 0,
    adjustmentAmount: 0,
    balanceAmount: 0,
  });

  const fetchCustomers = async () => {
    try {
      const response = await API.get("/customers");
      setCustomers(response.data || []);
    } catch (error) {
      message.error("Failed to fetch customers.");
      console.error("Error fetching customers:", error);
    }
  };

  const fetchCustomerPayments = async (customerId) => {
    try {
      const response = await API.get(`/payments/customer/${customerId}`, {
        params: { page: 1, limit: 20, search: "" },
      });
      const { totals } = response.data;
      setInvoiceData({
        totalAmount: totals.totalAmount || 0,
        paidAmount: totals.paidAmount || 0,
        adjustmentAmount: totals.adjustmentAmount || 0,
        balanceAmount: selectedPayment
          ? (totals.balanceAmount || 0) + (selectedPayment?.paid_amount || 0)
          : totals.balanceAmount || 0,
      });
    } catch (error) {
      message.error("Failed to fetch customer payment data.");
      console.error("Error fetching customer payments:", error);
      setInvoiceData({
        totalAmount: 0,
        paidAmount: 0,
        adjustmentAmount: 0,
        balanceAmount: 0,
      });
    }
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);
      if (selectedPayment) {
        await API.put(`/payments/${selectedPayment._id}`, {
          ...paymentData,
          ...(paymentData.transactionId
            ? { transactionId: paymentData.transactionId }
            : {}),
          customerId:
            selectedPayment.customerId?._id || selectedPayment.customerId,
        });
        message.success("Payment updated successfully.");
      } else {
        await API.post("/payments", {
          ...paymentData,
          adjustment_amount: paymentData.adjustment_amount || 0,
          customerId:
            selectedCustomer?.customerId?._id ||
            selectedCustomer?.selectedCustomer ||
            selectedCustomer,
          type: "pay",
        });
        message.success("Payment added successfully.");
      }
      setDrawerVisible(false);
      form.resetFields();
      setModal(false);
      handleSubmit();
    } catch (error) {
      console.error("Error processing payment:", error.response || error);
      message.error(
        error?.response?.data?.message || "Failed to process payment."
      );
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    try {
      setPaymentData(values);
      setModal(true);
    } catch (error) {
      message.error("Failed to process payment.");
      console.error("Error submitting payment:", error);
    }
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    form.resetFields();
    setInvoiceData({
      totalAmount: 0,
      paidAmount: 0,
      adjustmentAmount: 0,
      balanceAmount: 0,
    });
  };

  const validatePaymentLimit = (_, value) => {
    const paidAmount = parseFloat(form.getFieldValue("paid_amount")) || 0;
    const adjustmentAmount =
      parseFloat(form.getFieldValue("adjustment_amount")) || 0;
    const balanceAmount =
      invoiceData.balanceAmount + invoiceData.adjustmentAmount || 0;

    if (paidAmount + adjustmentAmount > balanceAmount) {
      return Promise.reject(
        new Error(
          `Paid Amount + Adjustment Amount (${
            paidAmount + adjustmentAmount
          }) cannot exceed Remaining Balance (${balanceAmount})`
        )
      );
    }
    return Promise.resolve();
  };

  // Fetch customers only once on mount
  useEffect(() => {
    fetchCustomers();
  }, []);

  // Fetch payment data when customer changes
  useEffect(() => {
    const customerId = selectedPayment
      ? selectedPayment.customerId?._id || selectedPayment.customerId
      : selectedCustomer?.customerId?._id ||
        selectedCustomer?.selectedCustomer ||
        selectedCustomer;

    if (customerId && open) {
      fetchCustomerPayments(customerId);
    } else {
      setInvoiceData({
        totalAmount: 0,
        paidAmount: 0,
        adjustmentAmount: 0,
        balanceAmount: 0,
      });
    }
  }, [selectedPayment, selectedCustomer, open]);

  // Update form fields when selectedPayment, selectedCustomer, or invoiceData changes
  useEffect(() => {
    if (!open) return; // Skip if drawer is closed

    if (selectedPayment) {
      // Editing an existing payment
      form.setFieldsValue({
        customerId:
          selectedPayment.customerId?._id || selectedPayment.customerId,
        paymentDate: dayjs(selectedPayment.paymentDate),
        paymentMethod: selectedPayment.paymentMethod,
        paid_amount: selectedPayment.paid_amount,
        adjustment_amount: selectedPayment.adjustment_amount || 0,
        bonus_amount: selectedPayment.bonus_amount || 0,
        transactionId: selectedPayment.transactionId,
        remarks: selectedPayment.remarks,
        invoiceId: selectedPayment.invoiceId || selectedPayment.transactionId,
      });
    } else if (selectedCustomer && invoiceData.balanceAmount !== 0) {
      // New payment for a selected customer/invoice
      const customerId =
        selectedCustomer?.customerId?._id ||
        selectedCustomer?.selectedCustomer ||
        selectedCustomer;

      // Calculate specific remaining if selectedCustomer has record data (specific invoice)
      let defaultPaid = invoiceData.balanceAmount;
      if (selectedCustomer.balance_amount && selectedCustomer.balancePaid) {
        defaultPaid =
          selectedCustomer.balance_amount - selectedCustomer.balancePaid;
      }
      form.setFieldsValue({
        customerId,
        paymentDate: dayjs(),
        invoiceId: selectedCustomer?.transactionId || "",
        paid_amount: invoiceData.balanceAmount,
        adjustment_amount: 0,
        bonus_amount: 0,
      });
    } else {
      form.resetFields();
    }
  }, [selectedPayment, selectedCustomer, invoiceData, form, open]);

  return (
    <div style={{ padding: "20px" }}>
      <Drawer
        title={selectedPayment ? "Edit Payment" : "Add Payment"}
        width="60vw"
        // style={{ maxWidth: "1000px" }}
        onClose={closeDrawer}
        open={open}
        bodyStyle={{ paddingBottom: 80 }}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={12} lg={8}>
              <Form.Item
                name="customerId"
                label="Select Customer"
                rules={[
                  { required: true, message: "Please select a customer!" },
                ]}
              >
                <Select
                  placeholder="Select Customer"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      ?.includes(input.toLowerCase())
                  }
                  style={{ width: "100%" }}
                  allowClear
                  disabled={true} // Disable since customer is pre-selected
                >
                  {customers.map((customer) => (
                    <Option key={customer?._id} value={customer?._id}>
                      {customer?.name || "Unnamed Customer"}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={8}>
              <Form.Item
                name="paymentDate"
                label="Payment Date"
                rules={[
                  { required: true, message: "Please select a payment date!" },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  disabled={selectedPayment}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={8}>
              <Form.Item
                name="paymentMethod"
                label="Payment Method"
                rules={[
                  {
                    required: true,
                    message: "Please select a payment method!",
                  },
                ]}
              >
                <Select placeholder="Select Payment Method">
                  <Option value="cash">Cash</Option>
                  <Option value="upi">UPI</Option>
                  <Option value="credit_card">Credit Card</Option>
                  <Option value="debit_card">Debit Card</Option>
                  <Option value="bank_transfer">Bank Transfer</Option>
                  <Option value="others">Others</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={8}>
              <Form.Item
                name="paid_amount"
                label="Paid Amount"
                rules={[
                  { required: true, message: "Please enter the paid amount!" },
                  { validator: validatePaymentLimit },
                ]}
              >
                <Input type="number" placeholder="Enter paid amount" min={0} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={8}>
              <Form.Item
                name="adjustment_amount"
                label="Adjustment Amount"
                rules={[{ validator: validatePaymentLimit }]}
              >
                <Input
                  type="number"
                  placeholder="Enter adjustment amount (optional)"
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={8}>
              <Form.Item name="bonus_amount" label="Bonus Amount">
                <Input
                  type="number"
                  placeholder="Enter bonus amount (optional)"
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={8}>
              <Form.Item name="transactionId" label="Transaction ID/Reference">
                <Input placeholder="Enter transaction ID/reference" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={8}>
              <Form.Item
                name="invoiceId"
                label="Invoice ID"
                rules={[
                  { required: true, message: "Please enter the invoice ID!" },
                ]}
              >
                <Input placeholder="Enter Sale Invoice" readOnly={true} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24} lg={24}>
              <Form.Item name="remarks" label="Remarks">
                <Input.TextArea
                  rows={2}
                  placeholder="Enter remarks (optional)"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
            <Col xs={24} sm={24} md={12} lg={6}>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  style={{ width: "100%" }}
                  loading={loading}
                >
                  Submit
                </Button>
              </Form.Item>
            </Col>
          </Row>

          {/* <Divider /> */}
          <Card
            title="Invoice Summary"
            bordered={false}
            style={{ backgroundColor: "#f6f6f6", marginTop: "16px" }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={12} lg={6}>
                <div style={{ textAlign: "center", padding: "12px" }}>
                  <h5
                    style={{
                      margin: "0 0 8px 0",
                      fontSize: "14px",
                      color: "#666",
                    }}
                  >
                    Total Amount
                  </h5>
                  <p
                    style={{
                      margin: "0",
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "#1890ff",
                    }}
                  >
                    ₹{invoiceData.totalAmount.toLocaleString()}
                  </p>
                </div>
              </Col>
              <Col xs={24} sm={12} md={12} lg={6}>
                <div style={{ textAlign: "center", padding: "12px" }}>
                  <h5
                    style={{
                      margin: "0 0 8px 0",
                      fontSize: "14px",
                      color: "#666",
                    }}
                  >
                    Paid Balance
                  </h5>
                  <p
                    style={{
                      margin: "0",
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "#52c41a",
                    }}
                  >
                    ₹{invoiceData.paidAmount.toLocaleString()}
                  </p>
                </div>
              </Col>
              <Col xs={24} sm={12} md={12} lg={6}>
                <div style={{ textAlign: "center", padding: "12px" }}>
                  <h5
                    style={{
                      margin: "0 0 8px 0",
                      fontSize: "14px",
                      color: "#666",
                    }}
                  >
                    Total Adjustment Amount
                  </h5>
                  <p
                    style={{
                      margin: "0",
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "#fa8c16",
                    }}
                  >
                    ₹{invoiceData.adjustmentAmount.toLocaleString()}
                  </p>
                </div>
              </Col>
              <Col xs={24} sm={12} md={12} lg={6}>
                <div style={{ textAlign: "center", padding: "12px" }}>
                  <h5
                    style={{
                      margin: "0 0 8px 0",
                      fontSize: "14px",
                      color: "#666",
                    }}
                  >
                    Remaining Balance
                  </h5>
                  <p
                    style={{
                      margin: "0",
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "#f5222d",
                    }}
                  >
                    ₹{invoiceData.balanceAmount.toLocaleString()}
                  </p>
                </div>
              </Col>
            </Row>
          </Card>
        </Form>

        <ConfirmationModal
          visible={modal}
          title="Update Confirmation"
          message="Do you really want to update this data?"
          onConfirm={handleConfirm}
          onCancel={() => setModal(false)}
          confirmText="Confirm"
          cancelText="Cancel"
        />
      </Drawer>
    </div>
  );
};

export default CustomerPaymentDrawer;
